import { Button, DatePicker, message, Table, Tag } from "antd";
import React, { useState } from "react";
import { DownloadOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import { selectBaseUrl, selectProdTestUrl } from "../../redux/slices/envSlice";
import moment from "moment";
import dayjs from "dayjs";
import { token } from "../../constants/Constants";
import { get_billing_report, get_billing_report_export } from "../../endpoints/API";

const BillingReport = () => {
  const baseUrl = useSelector(selectBaseUrl);
  const prodTestUrl = useSelector(selectProdTestUrl);
  const { RangePicker } = DatePicker;
  const [data, setData] = useState([]);
  const [selectedDates, setSelectedDates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });

  const fetchReportData = async (dates, page = pagination.current, pageSize = pagination.pageSize) => {
    try {
      setLoading(true);
      const response = await get_billing_report(dates?.[0], dates?.[1], page, pageSize);
      const result = response.data;
      setData(result.results);
      setPagination((prev) => ({ ...prev, total: result.count, current: page, pageSize }));
    } catch (error) {
      message.error(error?.response?.data?.error || "Error fetching report data");
    } finally {
      setLoading(false);
    }
  };

  const handleDateChange = (value, dateString) => {
    setSelectedDates(dateString);
    fetchReportData(dateString, 1, pagination.pageSize);
  };

  const handleTableChange = (paginationInfo) => {
    fetchReportData(selectedDates, paginationInfo.current, paginationInfo.pageSize);
  };

  const formatCSVValue = (value) => {
    if (!value) return '""';
    if (Array.isArray(value)) return `"${value.join(", ")}"`;
    if (typeof value === "object") {
      return `"${Object.values(value).map((v) => v ? moment(v).format("DD/MM/YYYY hh:mm A") : "").join(" | ")}"`;
    }
    return `"${value}"`;
  };

  const handleDownload = async () => {
    try {
      setLoading(true);
      const response = await get_billing_report_export(selectedDates?.[0], selectedDates?.[1]);
      const allData = response.data;
      const csvContent = [
        columns.map((col) => col.title).join(","),
        ...allData.map((row) =>
          columns.map((col) => {
            let value = row[col.dataIndex];
            if (col.dataIndex === "schedule_date_time" && value)
              return formatCSVValue(moment(value).format("DD/MM/YYYY hh:mm A"));
            return formatCSVValue(value);
          }).join(",")
        ),
      ].join("\n");
      const blob = new Blob([csvContent], { type: "text/csv" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = "BillingReport.csv";
      link.click();
    } catch (error) {
      message.error("Failed to download billing report");
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    { title: "Order Number", dataIndex: "order_number", key: "order_number", width: 120 },
    { title: "Placed Date", dataIndex: "place_date", key: "place_date", width: 150, render: (t) => t ? moment(t).format("DD/MM/YYYY hh:mm A") : "-" },
    { title: "Scheduled Date", dataIndex: "schedule_date_time", key: "schedule_date_time", width: 150, render: (t) => t ? moment(t).format("DD/MM/YYYY hh:mm A") : "-" },
    { title: "Registered User", dataIndex: "registeruser", key: "registeruser", width: 120, render: (t) => t || "-" },
    { title: "Location", dataIndex: "location", key: "location", width: 150, render: (t) => t || "-" },
    { title: "Patient Name", dataIndex: "patient_name", key: "patient_name", width: 130 },
    { title: "Patient Gender", dataIndex: "patient_gender", key: "patient_gender", width: 110 },
    { title: "Patient Ph No", dataIndex: "patient_phone", key: "patient_phone", width: 130, render: (t) => t ? `'${t}` : "-" },
    { title: "Patient DOB", dataIndex: "patient_dob", key: "patient_dob", width: 110, render: (t) => t || "-" },
    { title: "Patient Age", dataIndex: "patient_age", key: "patient_age", width: 90, render: (t) => t || "-" },
    { title: "Test ID", dataIndex: "test_id", key: "test_id", width: 140, render: (t) => Array.isArray(t) ? t.join(", ") : t || "-" },
    { title: "Test Name", dataIndex: "test_name", key: "test_name", width: 160, render: (t) => Array.isArray(t) ? t.join(", ") : t || "-" },
    {
      title: "Test Amount", dataIndex: "test_amount", key: "test_amount", width: 110,
      render: (text) => {
        if (typeof text === "string") {
          const total = text.split(",").map((n) => parseFloat(n.trim())).reduce((a, v) => a + v, 0);
          return total ? total : "-";
        }
        return text;
      },
    },
    { title: "Discount", dataIndex: "discount", key: "discount", width: 90 },
    {
      title: "B2B Total", dataIndex: "b2b_total", key: "b2b_total", width: 100,
      render: (text) => {
        if (typeof text === "string") {
          const total = text.split(",").map((n) => parseFloat(n.trim())).reduce((a, v) => a + v, 0);
          return total ? total : "-";
        }
        return text;
      },
    },
    { title: "Total Receivables", dataIndex: "total_receivables", key: "total_receivables", width: 130 },
    { title: "Wellness Executive", dataIndex: "wellness_executive", key: "wellness_executive", width: 150, render: (t) => t || "-" },
    { title: "Lab Name", dataIndex: "lab", key: "lab", width: 120, render: (t) => t || "-" },
    {
      title: "Report Generated Time", dataIndex: "patient_report_generated_times", key: "patient_report_generated_times", width: 180, ellipsis: true,
      render: (value) => {
        if (!value || typeof value !== "object") return "-";
        return Object.values(value).map((t) => moment(t).format("DD/MM/YYYY hh:mm A")).join(" | ");
      },
    },
  ];

  return (
    <div style={{ padding: "8px" }}>
      <div className="flex flex-col md:flex-row md:items-center gap-2 mb-2">
        <span className="font-semibold whitespace-nowrap">Total: <Tag>{pagination.total}</Tag></span>
        <div className="w-full md:flex-1 md:max-w-xs">
          <RangePicker
            format="YYYY-MM-DD"
            onChange={handleDateChange}
            disabledDate={(current) => current && current > dayjs().endOf("day")}
            style={{ width: "100%" }}
          />
        </div>
        <Button type="primary" onClick={handleDownload} disabled={!data.length} className="w-full md:w-auto">
          Download Report <DownloadOutlined />
        </Button>
      </div>
      <div style={{ overflowX: "auto", marginTop: 16 }}>
        <Table
          columns={columns}
          dataSource={data}
          loading={loading}
          rowKey={(_, i) => i}
          scroll={{ x: "max-content" }}
          size="small"
          pagination={{ current: pagination.current, pageSize: pagination.pageSize, total: pagination.total, showSizeChanger: true, responsive: true, size: "small" }}
          onChange={handleTableChange}
        />
      </div>
    </div>
  );
};

export default BillingReport;